import { useEffect, useState, Fragment } from 'react';

import { useRouter } from 'next/navigation';


import { Combobox } from '@headlessui/react'
import {

  CursorArrowRaysIcon, 
  CheckBadgeIcon, 

} from '@heroicons/react/24/outline'


import { 
  EllipsisHorizontalIcon, 
  ChevronUpDownIcon,
  MagnifyingGlassIcon, 
  XMarkIcon, 
  ChevronDownIcon,
  ChevronUpIcon

} from '@heroicons/react/20/solid'


import AppLogo from "../apps/logo";
import { classNames } from '@/lib/helpers';


const MAX_SOURCES_LOGO = 5
const MAX_APPS_FILTERED = 20
const MAX_APPS_IN_SEARCH_BOX = 10

type AppAction = { 
  name: string, 
  slug: string, 
  descriptions: {
    platform: string, 
    description: string, 
  }[],
  sourceApps: {
    name: string, 
    slug: string,
    image_updated_at: boolean
  }[] | null
} | undefined

type App = { id: number, name: string, slug: string, image_updated_at }


export default function AppActionsSelectorBox(
  { appA, 
    appB, 
    setAppA, 
    setAppB,
    actions, 
    type, 
    selected, 
    setSelected, 
    translations 
  }:
  { 
    appA: App | undefined, 
    appB: App | undefined, 
    setAppA: any, 
    setAppB: any,    
    actions: AppAction[] | undefined, 
    type: "triggers" | "actions", 
    selected: AppAction, 
    setSelected: any, 
    translations: any 
  } 
) {


  // We need both for redirect links when switching apps. 
  // App is app, connection could be both right and left 
  const [app, connection] =  (type === 'triggers') ? [appA, appB] : [appB, appA]
  
  const redirectConnectionPath = (appSel) => {
    if (type === 'triggers') return `/${appSel?.slug}/integrations/${appB?.slug}`
    return `/${appA?.slug}/integrations/${appSel?.slug}`
  }

  const redirectConnectionName = (appSel) => {
    if (type === 'triggers') return `${appSel?.name} + ${appB?.name} integration`
    return `${appA?.name} + ${appSel?.name} integration`
  }  

  const ActionIcon = (type === 'triggers') ?  CursorArrowRaysIcon : CheckBadgeIcon

  const isTriggers = (type === 'triggers') ? true : false 
  const someSelected = selected?.slug ? true : false  // at least 1 action acton selected 

  const [searchText, setSearchText] = useState('')
  const [actionsVisible, setActionsVisible] = useState(actions)
  const [listVisible, setListVisible] = useState(false)

  
  useEffect(() => {    
    const actionsFiltered = searchText ? actions?.filter(x => x?.name?.toLowerCase()?.includes(searchText?.toLowerCase()) ) : actions
    setActionsVisible(actionsFiltered)
  }, [searchText])


  // APPS SELECTOR 
  const [searchAppText, setSearchAppText] = useState('')  // Searching apps in Combo Box
  const [selectedApp, setSelectedApp] = useState<App | undefined>(app)  // Currently selected App 
  const [appSelectorTouched, setAppSelectorTouched] = useState(false)
  

  // const searchAppsDataUrl =  `${process.env.NEXT_PUBLIC_NEXT_API_URL}/search/searchData?types=apps` // LEGACY VERSION 
  const baseUrl = process.env.NEXT_PUBLIC_ENV === 'production' ? 'https://stackreaction.com' : 'http://localhost:3000'
  const baseAppID = app?.id
  const searchAppsDataUrl = `${baseUrl}/api-new/apps/${baseAppID}/connection-alternatives?limit=${MAX_APPS_IN_SEARCH_BOX}&text=${searchAppText}`
  
  const [appsData, setAppsData] = useState([])  
  const [appsVisible, setAppsVisible] = useState([app])


  useEffect(() => {
    (async () => {
      if ((searchAppText || appSelectorTouched)) {
        // && (appsData?.length === 0) // was a condition from legacy
        try {
          // const resp = await fetch(searchAppsDataUrl, { cache: 'force-cache' } );
          // const searchData = await resp.json();
          // const { apps=[] } = searchData
          // const appsExcludeCurrentApp = apps?.filter(x => x?.slug !== connection?.slug)
          // New way 
          const resp = await fetch(searchAppsDataUrl, { cache: 'no-store' } );
          const apps = await resp.json();          
          const appsExcludeCurrentApp = apps?.filter(x => x?.slug !== connection?.slug)
          setAppsData(appsExcludeCurrentApp);  
        } catch {}
    }
    })();
  }, [searchAppText, appSelectorTouched]);


  useEffect(() => {
    const appsFiltered = filterApps(appsData, searchAppText); 
    setAppsVisible(appsFiltered); 
  }, [searchAppText, appSelectorTouched, appsData]);


  const router = useRouter();  
  useEffect(() => {
    if (appA && appB && (selectedApp?.slug !== app?.slug)) {
      const redirectUrl = redirectConnectionPath(selectedApp)
      router.push(redirectUrl)
    }
    
    // Extra 
    if (type === "triggers") {
      setAppA(selectedApp)
    } else {
      setAppB(selectedApp)
    }
    
  }, [selectedApp]);    
  

  const getExternalDescription = (sourceApp) => {
    return selected?.descriptions?.find(x => x?.platform === sourceApp?.slug)?.description
  }
  

  return (
    <div className="rounded-lg border border-gray-200 mb-auto">


      {/* APP SELECTOR  */}        
      <Combobox 
        as="div" 
        value={selectedApp} 
        onChange={setSelectedApp}
        className="relative w-full "
      >
        
        <div className=" group p-5 flex items-center space-x-3 border-b border-gray-200 rounded-t-lg hover:bg-gray-100  ">
          
          <div className="flex-none  p-1 rounded-lg bg-white border border-gray-100">
            <AppLogo 
              app={app} 
              className="h-10 w-10 object-cover "
            />
          </div>

          <div className="flex-auto relative">

            <Combobox.Input
              className="w-full px-2 text-lg font-medium leading-6 text-gray-900 rounded-lg border border-transparent outline-none 
                focus:outline-none focus:text-gray-700 focus:border-crisp-600/50 
                focus:ring focus:ring-inset focus:ring-transparent"
              onChange={(event) => setSearchAppText(event.target.value)}
              displayValue={ (item: App) => (item.name) }                
            />

            <Combobox.Button 
              className="px-3 absolute inset-y-0 right-0   flex items-center focus:outline-none"
              onClick={() => {setAppSelectorTouched(true)}}
            >
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400 group-hover:text-crisp-600" aria-hidden="true" />
            </Combobox.Button>

          </div>
          
        </div>
        
        <Combobox.Options 
          className="w-full absolute z-10 max-h-72 overflow-y-auto bg-white border border-gray-200"
          // as="div" 
        >
          {/* scroll-py-3  */}
          {appsVisible?.map((item, index) => (
            <Combobox.Option
              // as="div" 
              key={index}
              value={item}
              className={({ active }) =>
                classNames(active ? 'bg-gray-100' : '', '  flex cursor-default select-none px-5 py-3')
              }
            >
              {({ active, selected }) => (
                <>
                  <div className="p-1 flex-none  rounded-lg bg-white border border-gray-100">
                    <AppLogo 
                      app={item} 
                      className="h-10 w-10 object-cover"
                    />
                  </div>
                  <div className="pl-3 py-1 flex-1 items-center ">
                    {/* flex-1 items-center */}

                    <p
                      className={classNames(active ? 
                        'text-gray-900' : 
                        'text-gray-700', 'text-sm font-medium line-clamp-1'
                        )}
                    >
                      {item?.name}
                    </p> 
                    <p className="text-sm text-gray-500 line-clamp-1 tracking-tight">
                      {redirectConnectionName(item)}
                    </p>
                  </div>
                </>
              )}
            </Combobox.Option>
          ))}
        </Combobox.Options>

      </Combobox>

        



      {/* ACTION SELECTOR */}
      <div className={classNames(someSelected ? 
        "bg-blue-50" : 
        "bg-white", 
        "rounded-b-lg")}
      >
        
        {/* Actions Selector Header - Logo, Title, Button */}
        <div className="flex items-center space-x-5 py-4 pl-5 pr-4">
          <div className="flex-none flex p-2 bg-crisp-500 items-center justify-center rounded-lg">
            <ActionIcon className="h-8 w-8 text-white" aria-hidden="true" />
          </div>
          
          <div className="flex-1 space-y-0.5 text-base font-medium leading-6 tracking-tight">
            
            <p className="text-lg font-medium leading-6 text-gray-900">
              { translations.subtitle }
            </p>

            <p className=" text-gray-700">
              {someSelected ? selected?.name : translations.selectTitle}
            </p>

          </div>
          
          <div className="relative ml-auto  text-gray-400 hover:text-gray-500">

            {someSelected ? (
              <button 
                onClick={() => setSelected(null)}
                className="block px-4 py-3.5 "
              >
                <span className="sr-only">
                  {translations.selectClearTitle}
                </span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>            
            ) : (
              <button 
                onClick={() => setListVisible(!listVisible)}
                className="block px-4 py-3.5 "
                // lg:hidden
              >
                <span className="sr-only">
                  {translations.selectTitle}
                </span>
                <ChevronUpDownIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            )}

          </div>
        </div>

        {/* Actions Selector - Description and ExtActions Description  */}

        {true && someSelected ? (
          <div className="space-y-1.5 px-5 py-4 border-t border-gray-200">
            {selected?.sourceApps?.map(sourceApp => (
              <>
                <div className="flex  space-x-1.5">
                  <div className="p-0.5 flex-none rounded-full bg-white">
                    <AppLogo 
                      app={sourceApp} 
                      className=" h-5 w-5 object-cover rounded-full"
                    />
                  </div>
                  
                  <span className="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
                    <svg 
                      className={classNames(isTriggers ? "fill-red-500" : "fill-green-500", "h-1.5 w-1.5 ")} 
                      viewBox="0 0 6 6" 
                      aria-hidden="true"
                    >
                      <circle cx={3} cy={3} r={3} />
                    </svg>
                    {sourceApp?.name}
                  </span>
                </div>
                
                <div className="text-gray-500 text-sm">
                  { getExternalDescription(sourceApp) }
                  {/* TODO - Put AppAction description Here  */}
                  {/* ALso about supported Workflow automation platforms */}
                </div>                  
              </>
            ))}

          </div> 
        ) : null}  

      </div>
            

      {/* Selection Block */}
      {/* <div className={classNames(listVisible && !someSelected ? "" : "hidden", "lg:block")}> */}

      <div 
        className={listVisible ? 
          (someSelected ? "hidden" : "block") : 
          (someSelected ? "hidden" : "hidden lg:block") }
      >

      {/* <div className={classNames(someSelected || !listVisible ? "hidden" : "hidden lg:block", "")}> */}
        {/* lg:block */}
        
        
        {/* <div className="text-sm text-gray-500 px-3 py-1 border-t border-b border-gray-200 bg-gray-50">
          {translations.selectTitle}
        </div> */}
        
        
        <div className="min-w-0 flex-1 border-t border-b border-gray-200">
          <div className=" relative flex items-stretch flex-grow">
            
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>

            <input
              className="block w-full pl-10 pr-3 py-2 text-sm border border-transparent leading-5 bg-white shadow-sm placeholder-gray-500 
                focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-crisp-600 focus:border-crisp-600 " 
              placeholder={translations.searchPlaceholder}              
              onChange={(e) => { setSearchText(e.target.value) }}
            />
          </div>
        </div>        

        <dl className={classNames(someSelected ? "" : "h-72", " px-3 py-3 text-sm leading-6 bg-white  rounded-b-lg overflow-auto space-y-2")}>
          
          {actionsVisible && actionsVisible?.length>0 ? (
            actionsVisible?.map((action, actionIdx) => (
              <button 
                key={actionIdx} 
                title={translations.selectTitle}
                className={classNames(action?.slug === selected?.slug ? 
                  "bg-blue-50 hover:bg-blue-100 " : 
                  "hover:bg-gray-200", 
                  "w-full items-center  flex gap-x-3 px-2 py-2 rounded-md ")}
                onClick={() => setSelected(action)}
              >
                <div className="text-gray-500 flex-none">
                  <AppLogo 
                    app={app} 
                    className="h-10 w-10 flex-none rounded-md bg-white object-cover ring-1 ring-gray-900/10"
                  />
                </div>              
                <div className="text-gray-500 line-clamp-2 leading-normal	 text-left flex-1">
                  <p className="font-semibold">
                    {action?.name} 
                  </p>
                  {action?.sourceApps && action?.sourceApps?.length>0 ? (
                    <p className="">
                      Automation Platforms: {action?.sourceApps?.length}
                    </p>                  
                  ) : null }
                </div>
                <div className="text-gray-800 ml-auto flex-none ">
                  <div className="isolate flex -space-x-2.5 overflow-hidden">
                    {action?.sourceApps?.slice(0, MAX_SOURCES_LOGO)?.map((sourceApp, index) => (
                      <div key={index} className="">
                        <AppLogo 
                          app={sourceApp} 
                          className={classNames(true ? `z-${50-index*10}` : "", 
                          "bg-white relative inline-block h-7 w-7 rounded-full p-0.5 "
                          )} 
                        />
                      </div>
                    ))}
                  </div>                
                  
                </div>
              </button>
            ))
          ) : 
            <p className="text-gray-700">
              {translations.searchNotFound}
            </p> 
          }


        </dl>
      </div>

    </div>
  )
}


const filterApps = (apps, text) => {
  if (!(apps && apps?.length > 0)) return []  
  return apps
  
  // // LEGACY VERSION - filter on client SIDE
  // if (!text) return apps

  // const textCleaned = text?.toLowerCase()?.trim()
  // const filteredApps = apps?.filter(app => {
  //   return app?.name?.toLowerCase()?.includes(textCleaned)
  // })
  // const appsCapped = filteredApps?.slice(0, MAX_APPS_FILTERED)
  // return appsCapped
}
